import { useMemo, useCallback, useState, useEffect } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useFetchActiveIncidents } from '@atlassian/jira-servicedesk-linked-object-incidents-store/src/controllers/index.tsx';
import fetchJson from '@atlassian/jira-issue-reliability-fetch/src/issue-view-as-json.tsx';
import {
	type FieldId,
	type FieldValue,
	type UseResolveFieldValuesByIdType,
	type ValueType,
	type ServiceDetails,
	toValueType,
	toString,
	type UseResolveFieldValuesByIdResultType,
	type RestResult,
} from '../../common/types/index.tsx';
import { useGetServicesById } from '../get-services-by-id/main.tsx';

// This is used behind a feature flag in `src/packages/issue/fields/service-entity/src/ui/main.tsx`.
// When cleaning up the `isCompassJsmIntegrationEnabled` feature flag to fully replace
// the REST call with the GraphQL query, consider keeping the REST code, especially anything that would help
// with migrating to the Assets REST API in the future.
// See: https://atlassian.slack.com/archives/C01RC9H7GAK/p1682472661462269?thread_ts=1682053618.660039&cid=C01RC9H7GAK
export const useResolveFieldValuesById: UseResolveFieldValuesByIdType<ServiceDetails> = (
	fieldId: FieldId,
	fieldValues: FieldValue[] | null,
) => {
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | undefined>(undefined);
	const [data, setData] = useState<
		UseResolveFieldValuesByIdResultType<ServiceDetails>[] | undefined
	>(undefined);
	const { fetchActiveIncidentsForServices } = useFetchActiveIncidents();

	useEffect(() => {
		if (!fieldValues) {
			setData([]);
			setIsLoading(false);
			return;
		}

		// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
		const ids = fieldValues.map((value: FieldValue) => value.id);

		fetchJson(
			'/rest/service-registry-api/service?query=',
			{
				method: 'POST',
				body: JSON.stringify({
					ids,
				}),
			},
			'fields/service-entity/src/services/resolve-field-values-by-id',
		)
			.then((result: RestResult) => {
				const values: (ValueType<ServiceDetails> | Error)[] = result.services.map(toValueType);
				setData(values);
				setIsLoading(false);
			})
			.catch((caughtError: Error) => {
				setIsLoading(false);
				setError(caughtError);
				fireErrorAnalytics({
					meta: {
						id: 'useResolveFieldValuesById',
						packageName: 'jiraIssueFieldServiceEntity',
						teamName: 'rohirrim',
					},
					attributes: {
						dataFetchingApproach: 'rest',
					},
					error: caughtError,
				});
			});

		fetchActiveIncidentsForServices(toString(fieldId), ids);

		// Disabling rule ensure that effect runs only if id really
		// changes not only the reference

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(fieldValues)]);
	return {
		isLoading,
		data,
		error,
	};
};

export const useResolveFieldValuesByIdGraphQL: UseResolveFieldValuesByIdType<ServiceDetails> = (
	fieldId: FieldId,
	fieldValues: FieldValue[] | null,
) => {
	const { fetchActiveIncidentsForServices } = useFetchActiveIncidents();

	const ids = useMemo(() => fieldValues?.map((value: FieldValue) => value.id) ?? [], [fieldValues]);

	const onCompleted = useCallback(
		() => fetchActiveIncidentsForServices(toString(fieldId), ids),
		// Disabling rule ensure that effect runs only if id really
		// changes not only the reference

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[JSON.stringify(ids)],
	);

	const onError = useCallback(
		(err: Error | undefined) =>
			fireErrorAnalytics({
				meta: {
					id: 'useResolveFieldValuesById',
					packageName: 'jiraIssueFieldServiceEntity',
					teamName: 'rohirrim',
				},
				attributes: {
					dataFetchingApproach: 'graphql',
				},
				error: err,
			}),
		[],
	);

	const { data, loading, error } = useGetServicesById({
		ids,
		onCompleted,
		onError,
	});

	const results: ValueType<ServiceDetails>[] = useMemo(
		() =>
			data?.devOpsServicesById?.map((service) => {
				// The tier always exists but the query API has it as nullable
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const transformedService = { ...service, tier: service.serviceTier! };
				return toValueType(transformedService);
			}) ?? [],
		[data],
	);

	return {
		data: results,
		isLoading: loading,
		error,
	};
};
